import { useState, useEffect, useCallback } from 'react';

import InsertInvitationRoundedIcon from '@mui/icons-material/InsertInvitationRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';

import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import { useProductLandingContext } from '../../contexts/useProductLanding';

const SCROLL_THRESHOLD = 0.1;

export const AssistanceStickySection = () => {
  const { productConfig } = useProductLandingContext();
  const { assistanceSticky } = productConfig;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isVisible, setIsVisible] = useState(false);

  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;

    if (docHeight > 0) {
      setIsVisible(scrollTop / docHeight >= SCROLL_THRESHOLD);
    }
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll, isMobile]);

  if (!assistanceSticky || (isMobile && !isVisible)) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: { xs: 64, md: 24 },
        zIndex: 10,
        backgroundColor: 'background.grey.darker',
        borderRadius: 2,
        boxShadow: '0px 16px 32px -8px rgba(15, 22, 28, 0.12), 0px 8px 16px -4px rgba(15, 22, 28, 0.06)',
        padding: { xs: 2, md: 2.5 }
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        gap={2}
      >
        <Text size="md" weight="medium" color="primary">
          <FormattedMessage id={assistanceSticky.title} />
        </Text>
        <Stack direction="row" gap={1.5} sx={{ width: { xs: '100%', md: 'auto' } }}>
          <Button
            variant="outlined"
            size="md"
            fullWidth
            href={assistanceSticky.primaryAction.href}
            target="_blank"
            startIcon={<PhoneRoundedIcon />}
            clickstreamProps={{
              elementName: 'assistance_sticky_primary_action',
              destination: assistanceSticky.primaryAction.href
            }}
          >
            <FormattedMessage id={assistanceSticky.primaryAction.title} />
          </Button>
          <Button
            variant="contained"
            size="md"
            fullWidth
            href={assistanceSticky.secondaryAction.href}
            target="_blank"
            startIcon={<InsertInvitationRoundedIcon />}
            clickstreamProps={{
              elementName: 'assistance_sticky_secondary_action',
              destination: assistanceSticky.secondaryAction.href
            }}
          >
            <FormattedMessage id={assistanceSticky.secondaryAction.title} />
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
